<template>
  <div class="p-4">
    <h2 class="pb-4 font-medium text-gray-800">{{ $t('pages.roles') }}</h2>
    <div class="pt-4">
      <BaseTable
        v-if="roles"
        :headers="tableHeaders"
        :list="roles"
        table-id="roles"
        :clickable="true"
        @refresh-data="refreshData"
        @deleteMultiselect="onDeleteMultiselect"
        @tableItemClicked="onEdit"
        @add-new="router.push(`/settings/role/${uuidv4()}`)"
        hide-grouping
        is-saving-layout-hidden
        is-download-button-hidden
        @apply-filters="onApplyFilters"
        @reset-filters="onResetFilters">
        <template #actions="{ item }">
          <div class="flex items-center justify-center gap-1">
            <ActionButton
              :isEnabled="item.isFromManager && !item.deleted"
              color="red"
              :icon="IconTrashOutline"
              @action="onDeleteRow(item as Role)" />
          </div>
        </template>
        <template #filters>
          <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('baseObject.isActive')
              }}</label>
              <BaseTriStateToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                v-model="filterIsActive" />
            </div>
          </section>
        </template>
      </BaseTable>
      <BaseModalConfirm
        v-if="modalDeleteRow"
        @cancel="modalDeleteRow = false"
        @confirm="onDeleteSingle()" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import BaseTable from '@/components/common/BaseTable.vue';
import ActionButton from '@/components/common/ActionButton.vue';

import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import type { Role } from '@/types/Roles';
import { getAllRoles, removeRolesByIds } from '@/services/api/user';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useRouter } from 'vue-router';
import { v4 as uuidv4 } from 'uuid';

const roles = ref<Role[]>();

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];
const router = useRouter();
const filterIsActive = ref<boolean | undefined>();
onMounted(async () => {
  roles.value = await getAllRoles();
});
const toastStore = useToastStore();
async function onDeleteMultiselect(items: Role[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.error({ text: 'Selected items cannot be deleted.' });
    return;
  }

  const idsToDelete = itemsToDelete.map((item) => item.id);
  const res = await removeRolesByIds(idsToDelete);
  if (!res && roles.value)
    roles.value = roles.value.filter((role) => !idsToDelete.includes(role.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function refreshData(data: TableFilterData) {
  roles.value = await getAllRoles();

  if (data.query) {
    roles.value = filterData(roles.value, data.query, ['name', 'code']);
  }
  if (data.column !== undefined && data.order) {
    roles.value = sortData(roles.value, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined) roles.value = filterActive(roles.value, data.isActive);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeRolesByIds([idToDelete]);
  if (!res && roles.value)
    roles.value = roles.value.map((role) => {
      if (role.id == idToDelete) {
        role.deleted = true;
      }
      return role;
    });
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<Role>();
function onDeleteRow(item: Role) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onEdit(role: Role) {
  router.push(`/settings/role/${role.id}`);
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
